import type { ObjectId } from 'mongodb';

import type { DocumentApiResponse, DocumentUploadFields } from './DocumentTypes';
import type { Meta } from './Entities';

export const SSE_CHANNEL_DOCUMENT_UPLOAD = 'document-upload';

export enum DocumentUploadEventType {
  queued = 'queued',
  awaitingUpload = 'awaitingUpload',
  processing = 'processing',
  complete = 'complete',
  imageVariantsReady = 'imageVariantsReady',
  failed = 'failed',
}

export interface DocumentUploadQueuedEvent {
  type: DocumentUploadEventType.queued;
  jobId: string;
}

export interface DocumentUploadAwaitingUploadEvent {
  type: DocumentUploadEventType.awaitingUpload;
  jobId: string;
}

export interface DocumentUploadProcessingEvent {
  type: DocumentUploadEventType.processing;
  jobId: string;
}

export interface DocumentUploadCompleteEvent {
  type: DocumentUploadEventType.complete;
  jobId: string;
  documents: DocumentApiResponse[];
}

export interface DocumentUploadImageVariantsReadyEvent {
  type: DocumentUploadEventType.imageVariantsReady;
  jobId: string;
  documents: DocumentApiResponse[];
}

export interface DocumentUploadFailedEvent {
  type: DocumentUploadEventType.failed;
  jobId: string;
  error: string;
}

export type DocumentUploadEvent =
  | DocumentUploadQueuedEvent
  | DocumentUploadAwaitingUploadEvent
  | DocumentUploadProcessingEvent
  | DocumentUploadCompleteEvent
  | DocumentUploadImageVariantsReadyEvent
  | DocumentUploadFailedEvent;

export interface DocumentUploadJobFile {
  filename: string;
  stagingPath: string;
  uploadUrl: string;
  expiresAt: string;
  // Canonical contentType the server derived from the extension and signed
  // the upload URL with. Clients must use this exact value as the PUT
  // `Content-Type` header — anything else breaks the signature. Optional
  // for older API versions that didn't return it.
  contentType?: string;
}

export interface DocumentUploadJobResponse {
  jobId: string;
  status: DocumentUploadEventType.awaitingUpload | DocumentUploadEventType.queued;
  files?: DocumentUploadJobFile[];
}

export interface DocumentUploadInitRequestFile {
  filename: string;
  // Client hint only. The server picks the canonical contentType for the
  // extension. Browsers report HEIC as `image/heic`, `image/heif`, or
  // empty string depending on platform, so we cannot trust the client.
  contentType?: string;
  size: number;
}

export interface DocumentUploadInitRequest {
  files: DocumentUploadInitRequestFile[];
  type?: string;
  fields?: DocumentUploadFields;
}

/**
 * Response shape of the v2 polling endpoint
 * `GET /api/v2/accounts/:accountId/documents/jobs/:jobId`. The endpoint
 * returns the latest cached event payload directly — no wrapping envelope.
 */
export type DocumentUploadJobStatusResponse = DocumentUploadEvent;

/** Lifecycle status of a v2 document upload manifest row. */
export enum DocumentUploadJobStatus {
  /** Manifest written; shepherd is polling for staged files. */
  awaitingStaging = 'awaitingStaging',
  /** All expected files present; promotion job has been enqueued (CAS-won). */
  readyForPromotion = 'readyForPromotion',
  /** Promotion is running. */
  processing = 'processing',
  /** Promotion finished successfully. */
  complete = 'complete',
  /** Schedule exhausted before files arrived, or promotion failed terminally. */
  failed = 'failed',
}

export interface DocumentUploadJobStagedFile {
  stagingPath: string;
  contentType: string;
  originalName: string;
  originalExtension: string;
}

/**
 * Mongo document for the `documentUploadJobs` collection — authoritative
 * manifest for a v2 upload. The Bull shepherd job only carries `{ jobId }`;
 * everything else is read from this row.
 */
export interface DocumentUploadJobDocument {
  /** Mongo `_id`; entity helpers also surface this as `id`. */
  _id?: ObjectId;
  id?: ObjectId;
  /** Equal to the nanoid jobId the API returns to the client. */
  jobId: string;
  accountId: ObjectId;
  applicationId?: ObjectId;
  documentType: string;
  fields: DocumentUploadFields & { type: string };
  stipulationType?: string;
  grouped?: boolean;
  stagedFiles: DocumentUploadJobStagedFile[];
  status: DocumentUploadJobStatus;
  /** Index into SHEPHERD_DELAY_SCHEDULE_MS; incremented when shepherd reschedules. */
  shepherdAttemptIndex?: number;
  /** Set when shepherd CAS-wins the awaitingStaging → readyForPromotion transition. */
  readyDispatchedAt?: Date;
  /** Latest error message when status === failed. */
  errorMessage?: string;
  /** Set on TTL index so Mongo cleans manifests up automatically. */
  createdAt: Date;
  meta: Meta;
}
